import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  FactCheck as FactCheckIcon,
  ContentPaste as ContentPasteIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatDateTimeBR } from '../utils/dateUtils';

const SITUACAO_OPTIONS = [
  'Verificar',
  'Verificado',
  'Em Transporte',
  'Não Localizado',
  'Impressão',
  'Corte',
  'Recorte',
] as const;

// Cores semânticas por situação (chip)
const SITUACAO_COLORS: Record<string, { bg: string; color: string }> = {
  'Verificar': { bg: '#fef3c7', color: '#b45309' },
  'Verificado': { bg: '#dcfce7', color: '#15803d' },
  'Em Transporte': { bg: '#dbeafe', color: '#1d4ed8' },
  'Não Localizado': { bg: '#fee2e2', color: '#b91c1c' },
  'Impressão': { bg: '#ede9fe', color: '#6d28d9' },
  'Corte': { bg: '#cffafe', color: '#0e7490' },
  'Recorte': { bg: '#ffedd5', color: '#c2410c' },
};

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2MB

interface Verification {
  id: string;
  orderNumber: string;
  phone: string;
  situacao: string;
  observation?: string | null;
  thumbnail?: string | null;
  attachmentName?: string | null;
  attachmentSize?: number | null;
  hasAttachment?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  orderNumber: string;
  phone: string;
  situacao: string;
  observation: string;
  // undefined = anexo inalterado; null = removido; string = novo base64
  attachment?: string | null;
  thumbnail?: string | null;
  attachmentName?: string | null;
}

const emptyForm: FormData = {
  orderNumber: '',
  phone: '',
  situacao: 'Verificar',
  observation: '',
  attachment: undefined,
  thumbnail: undefined,
  attachmentName: undefined,
};

// Gera uma miniatura leve (JPEG comprimido) a partir de um data URL de imagem.
const makeThumbnail = (dataUrl: string, maxSize = 160): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve('');
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      } catch {
        resolve('');
      }
    };
    img.onerror = () => resolve('');
    img.src = dataUrl;
  });

const formatFileSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const VerificarPedido: React.FC = () => {
  const [rows, setRows] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [situacaoFilter, setSituacaoFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  // Dialogs
  const [formDialog, setFormDialog] = useState<{ open: boolean; mode: 'add' | 'edit'; id?: string }>({
    open: false,
    mode: 'add',
  });
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [situacaoDialog, setSituacaoDialog] = useState<{ open: boolean; id?: string; current?: string }>({
    open: false,
  });

  const [viewDialog, setViewDialog] = useState<{ open: boolean; record?: any; loading: boolean }>({
    open: false,
    loading: false,
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setSnackbar({ open: true, message, severity });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllOrderVerifications(
        startDate || undefined,
        endDate || undefined,
        situacaoFilter || undefined,
      );
      setRows(data || []);
    } catch (error) {
      console.error('Erro ao carregar verificações:', error);
      showSnackbar('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, situacaoFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---- Filtro em memória (busca por nº pedido, telefone ou observação) ----
  const filtered = rows.filter((r) => {
    const term = searchTerm.toLowerCase();
    return (
      r.orderNumber.toLowerCase().includes(term) ||
      r.phone.toLowerCase().includes(term) ||
      (r.observation || '').toLowerCase().includes(term)
    );
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ---- Seleção múltipla ----
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelected(new Set(filtered.map((r) => r.id)));
    else setSelected(new Set());
  };
  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  // ---- Aplica um arquivo de imagem (validação + base64). Usado pelo input, pelo Ctrl+V e pelo botão Colar. ----
  const applyImageFile = async (file: File) => {
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      showSnackbar('Anexo deve ser PNG ou JPG', 'warning');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      showSnackbar('Anexo excede o limite de 2MB', 'warning');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      const thumbnail = await makeThumbnail(base64);
      // Imagens coladas costumam vir sem nome — gera um padrão
      const name = file.name && file.name.trim() ? file.name : `print-${Date.now()}.png`;
      setForm((f) => ({ ...f, attachment: base64, thumbnail, attachmentName: name }));
    } catch {
      showSnackbar('Erro ao ler o arquivo', 'error');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reanexar o mesmo arquivo
    if (file) applyImageFile(file);
  };

  // Botão "Colar": lê a imagem da área de transferência (Chrome/Edge em https/localhost)
  const handlePasteButton = async () => {
    try {
      if (!navigator.clipboard || !(navigator.clipboard as any).read) {
        showSnackbar('Use Ctrl+V para colar a imagem', 'info');
        return;
      }
      const items = await (navigator.clipboard as any).read();
      for (const item of items) {
        const type = (item.types as string[]).find((t) => t.startsWith('image/'));
        if (type) {
          const blob = await item.getType(type);
          const file = new File([blob], `print-${Date.now()}.png`, { type: blob.type });
          await applyImageFile(file);
          showSnackbar('Imagem colada!', 'success');
          return;
        }
      }
      showSnackbar('Nenhuma imagem na área de transferência', 'warning');
    } catch {
      showSnackbar('Não foi possível colar automaticamente. Use Ctrl+V.', 'info');
    }
  };

  // Colar com Ctrl+V enquanto o modal está aberto (captura só imagens; texto continua normal)
  useEffect(() => {
    if (!formDialog.open) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            applyImageFile(file);
            showSnackbar('Imagem colada!', 'success');
            return;
          }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDialog.open]);

  // ---- Abrir dialog de criar ----
  const openAdd = () => {
    setForm(emptyForm);
    setFormDialog({ open: true, mode: 'add' });
  };

  // ---- Abrir dialog de editar ----
  const openEdit = (r: Verification) => {
    setForm({
      orderNumber: r.orderNumber,
      phone: r.phone,
      situacao: r.situacao,
      observation: r.observation || '',
      attachment: undefined, // inalterado até o usuário anexar/remover
      attachmentName: r.attachmentName || undefined,
    });
    setFormDialog({ open: true, mode: 'edit', id: r.id });
  };

  const handleSaveForm = async () => {
    if (!form.orderNumber.trim() || !form.phone.trim()) {
      showSnackbar('Preencha o número do pedido e o telefone', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (formDialog.mode === 'add') {
        await apiService.createOrderVerification({
          orderNumber: form.orderNumber.trim(),
          phone: form.phone.trim(),
          situacao: form.situacao,
          observation: form.observation || null,
          attachment: typeof form.attachment === 'string' ? form.attachment : null,
          thumbnail: typeof form.attachment === 'string' ? form.thumbnail || null : null,
          attachmentName: typeof form.attachment === 'string' ? form.attachmentName || 'anexo' : null,
        });
        showSnackbar('Pedido cadastrado com sucesso!', 'success');
      } else if (formDialog.id) {
        const payload: any = {
          orderNumber: form.orderNumber.trim(),
          phone: form.phone.trim(),
          situacao: form.situacao,
          observation: form.observation || null,
        };
        // Só envia o anexo se foi alterado (novo arquivo ou remoção)
        if (form.attachment !== undefined) {
          payload.attachment = form.attachment; // string (novo) ou null (removido)
          payload.thumbnail = form.attachment ? form.thumbnail || null : null;
          payload.attachmentName = form.attachment ? form.attachmentName || 'anexo' : null;
        }
        await apiService.updateOrderVerification(formDialog.id, payload);
        showSnackbar('Pedido atualizado com sucesso!', 'success');
      }
      setFormDialog({ open: false, mode: 'add' });
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao salvar', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Mudar situação via modal ----
  const handleChangeSituacao = async (novaSituacao: string) => {
    if (!situacaoDialog.id) return;
    try {
      await apiService.updateOrderVerification(situacaoDialog.id, { situacao: novaSituacao });
      setSituacaoDialog({ open: false });
      showSnackbar('Situação atualizada!', 'success');
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao atualizar situação', 'error');
    }
  };

  // ---- Visualizar (carrega o registro completo com o anexo) ----
  const openView = async (id: string) => {
    setViewDialog({ open: true, loading: true });
    try {
      const record = await apiService.getOrderVerificationById(id);
      setViewDialog({ open: true, loading: false, record });
    } catch {
      setViewDialog({ open: false, loading: false });
      showSnackbar('Erro ao carregar o pedido', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este registro?')) return;
    try {
      await apiService.deleteOrderVerification(id);
      showSnackbar('Registro excluído', 'success');
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const handleDeleteMultiple = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} registro(s)?`)) return;
    try {
      await apiService.deleteMultipleOrderVerifications(Array.from(selected));
      showSnackbar(`${selected.size} registro(s) excluído(s)`, 'success');
      setSelected(new Set());
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const situacaoChip = (situacao: string, onClick?: () => void) => {
    const c = SITUACAO_COLORS[situacao] || { bg: '#e5e7eb', color: '#374151' };
    return (
      <Chip
        label={situacao}
        size="small"
        onClick={onClick}
        sx={{
          backgroundColor: c.bg,
          color: c.color,
          fontWeight: 700,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { filter: 'brightness(0.95)' } : undefined,
        }}
      />
    );
  };

  const colSpan = 8;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactCheckIcon sx={{ color: '#D4AF37' }} /> Verificar Pedido
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Consulte, verifique e acompanhe pedidos informando situação, observação e anexo (print/PNG).
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600, px: 3, py: 1.5, '&:hover': { backgroundColor: '#B8941F' } }}
        >
          Novo
        </Button>
      </Box>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nº do pedido ou telefone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Situação</InputLabel>
                <Select value={situacaoFilter} label="Situação" onChange={(e) => setSituacaoFilter(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {SITUACAO_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data inicial"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data final"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                sx={{ borderColor: '#D4AF37', color: '#B8941F', height: 40, '&:hover': { borderColor: '#B8941F', backgroundColor: 'rgba(212,175,55,0.04)' } }}
              >
                Atualizar
              </Button>
              {selected.size > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteMultiple}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  {selected.size}
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                <TableCell padding="checkbox" sx={{ px: 1 }}>
                  <Checkbox
                    checked={selected.size === filtered.length && filtered.length > 0}
                    indeterminate={selected.size > 0 && selected.size < filtered.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>Nº DO PEDIDO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>TELEFONE</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>SITUAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>OBSERVAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>ANEXO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F', textAlign: 'center' }}>AÇÕES</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>DATA/HORA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>Nenhum pedido encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox" sx={{ px: 1 }}>
                      <Checkbox checked={selected.has(r.id)} onChange={() => handleSelectOne(r.id)} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.orderNumber}</TableCell>
                    <TableCell>{r.phone}</TableCell>
                    <TableCell>
                      {situacaoChip(r.situacao, () => setSituacaoDialog({ open: true, id: r.id, current: r.situacao }))}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Tooltip title={r.observation || ''}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.observation || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {r.hasAttachment ? (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                          onClick={() => openView(r.id)}
                        >
                          <Box sx={{ width: 64, height: 64, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0, border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {r.thumbnail ? (
                              <img src={r.thumbnail} alt={r.attachmentName || 'anexo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <ImageIcon sx={{ color: '#9ca3af', fontSize: 28 }} />
                            )}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', lineHeight: 1.2 }}>
                              {r.attachmentName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                              {formatFileSize(r.attachmentSize)}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          onClick={() => openEdit(r)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1,
                            border: '1px dashed #d1d5db', color: '#9ca3af', cursor: 'pointer', maxWidth: 200,
                            '&:hover': { borderColor: '#D4AF37', color: '#B8941F' },
                          }}
                        >
                          <CloudUploadIcon sx={{ fontSize: 20 }} />
                          <Box>
                            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1 }}>Anexar imagem</Typography>
                            <Typography variant="caption" sx={{ fontSize: 10 }}>PNG ou JPG (máx. 2MB)</Typography>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Visualizar">
                        <IconButton size="small" onClick={() => openView(r.id)} sx={{ color: '#D4AF37' }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#B8941F' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#ef4444' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                      {formatDateTimeBR(r.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>

      {/* Dialog: Criar / Editar */}
      <Dialog open={formDialog.open} onClose={() => setFormDialog({ open: false, mode: 'add' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          {formDialog.mode === 'add' ? 'Novo Pedido' : 'Editar Pedido'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Nº do Pedido" required
                value={form.orderNumber}
                onChange={(e) => setForm({ ...form, orderNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Telefone" required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Situação</InputLabel>
                <Select value={form.situacao} label="Situação" onChange={(e) => setForm({ ...form, situacao: e.target.value })}>
                  {SITUACAO_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Observação" multiline rows={3}
                value={form.observation}
                onChange={(e) => setForm({ ...form, observation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>Anexo (print)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ borderColor: '#D4AF37', color: '#B8941F' }}>
                  Escolher imagem
                  <input hidden type="file" accept="image/png,image/jpeg" onChange={handleFileChange} />
                </Button>
                <Button variant="outlined" startIcon={<ContentPasteIcon />} onClick={handlePasteButton} sx={{ borderColor: '#D4AF37', color: '#B8941F' }}>
                  Colar
                </Button>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>PNG ou JPG, máx. 2MB · ou cole com <strong>Ctrl+V</strong></Typography>
              </Box>
              {/* Estado do anexo */}
              {form.attachment ? (
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src={form.attachment} alt="preview" style={{ height: 48, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                  <Typography variant="body2">{form.attachmentName}</Typography>
                  <IconButton size="small" onClick={() => setForm({ ...form, attachment: null, attachmentName: null })} sx={{ color: '#ef4444' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : formDialog.mode === 'edit' && form.attachment === undefined && form.attachmentName ? (
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon sx={{ color: '#9ca3af' }} />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>Anexo atual: {form.attachmentName}</Typography>
                  <Button size="small" color="error" onClick={() => setForm({ ...form, attachment: null, attachmentName: null })}>
                    Remover
                  </Button>
                </Box>
              ) : form.attachment === null ? (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#ef4444' }}>Anexo será removido ao salvar.</Typography>
              ) : null}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormDialog({ open: false, mode: 'add' })} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleSaveForm}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#D4AF37', color: '#000', '&:hover': { backgroundColor: '#B8941F' } }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Situação (modal ao clicar no chip) */}
      <Dialog open={situacaoDialog.open} onClose={() => setSituacaoDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>Alterar Situação</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {SITUACAO_OPTIONS.map((s) => {
              const c = SITUACAO_COLORS[s];
              const isCurrent = s === situacaoDialog.current;
              return (
                <Button
                  key={s}
                  onClick={() => handleChangeSituacao(s)}
                  sx={{
                    justifyContent: 'flex-start',
                    backgroundColor: c.bg,
                    color: c.color,
                    fontWeight: 700,
                    border: isCurrent ? `2px solid ${c.color}` : '2px solid transparent',
                    '&:hover': { backgroundColor: c.bg, filter: 'brightness(0.95)' },
                  }}
                >
                  {s}{isCurrent ? '  (atual)' : ''}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSituacaoDialog({ open: false })} sx={{ color: '#666' }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Visualizar */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, loading: false })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: '#D4AF37', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalhes do Pedido
          <IconButton onClick={() => setViewDialog({ open: false, loading: false })} sx={{ color: '#D4AF37' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : viewDialog.record ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Nº do Pedido</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.record.orderNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Telefone</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.record.phone}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Situação</Typography>
                <Box sx={{ mt: 0.5 }}>{situacaoChip(viewDialog.record.situacao)}</Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Data/Hora</Typography>
                <Typography variant="body2">{formatDateTimeBR(viewDialog.record.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Observação</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewDialog.record.observation || '—'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>Anexo</Typography>
                {viewDialog.record.attachment ? (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={viewDialog.record.attachment}
                      alt={viewDialog.record.attachmentName || 'anexo'}
                      style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: '#9ca3af', mt: 0.5 }}>
                      {viewDialog.record.attachmentName} · {formatFileSize(viewDialog.record.attachmentSize)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>Sem anexo</Typography>
                )}
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VerificarPedido;
